import { useState } from 'react'
import { RedFlag, Alert } from '@/types'

interface RedFlagsPanelProps {
  redFlags: RedFlag[]
  alerts?: Alert[]
  className?: string
  showFilters?: boolean
  maxItems?: number
}

const severityColors = {
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
}

const statusColors = {
  active: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  investigating: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
}

const redFlagIcons = {
  name_mismatch: '🏷️',
  gst_non_filing: '📋',
  review_fraud: '⭐',
  financial_distress: '💰',
  transaction_anomaly: '📊',
  compliance_breach: '⚖️'
}

export function RedFlagsPanel({ 
  redFlags, 
  alerts = [], 
  className = '', 
  showFilters = true,
  maxItems 
}: RedFlagsPanelProps) {
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [showResolved, setShowResolved] = useState(false)

  // Filter red flags based on selected criteria
  const filteredRedFlags = redFlags.filter(flag => {
    if (selectedSeverity !== 'all' && flag.severity !== selectedSeverity) return false
    if (selectedStatus !== 'all' && flag.status !== selectedStatus) return false
    if (!showResolved && flag.status === 'resolved') return false
    return true
  })

  // Limit items if maxItems is specified
  const displayedRedFlags = maxItems ? filteredRedFlags.slice(0, maxItems) : filteredRedFlags

  // Sort by severity and date
  const sortedRedFlags = displayedRedFlags.sort((a, b) => {
    const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 }
    const severityDiff = severityOrder[b.severity] - severityOrder[a.severity]
    if (severityDiff !== 0) return severityDiff
    return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
  })

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 48) return 'Yesterday'
    return date.toLocaleDateString()
  }

  const getImpactText = (impact: number) => {
    if (impact === 0) return 'No impact'
    return impact > 0 ? `+${impact} points` : `${impact} points`
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Red Flags & Alerts
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Issues that may impact your TLBH score
            </p>
          </div>
          {redFlags.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                redFlags.some(f => f.severity === 'critical') ? severityColors.critical :
                redFlags.some(f => f.severity === 'high') ? severityColors.high :
                redFlags.some(f => f.severity === 'medium') ? severityColors.medium :
                severityColors.low
              }`}>
                {redFlags.filter(f => f.status === 'active').length} Active
              </span>
            </div>
          )}
        </div>

        {/* Filters */}
        {showFilters && redFlags.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 mb-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center space-x-2">
              <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                Severity:
              </label>
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white flex-1 sm:flex-none"
              >
                <option value="all">All</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                Status:
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white flex-1 sm:flex-none"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showResolved"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <label htmlFor="showResolved" className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                Show resolved
              </label>
            </div>
          </div>
        )}

        {/* Red Flags List */}
        {sortedRedFlags.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-green-600 dark:text-green-400 text-2xl">✓</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Red Flags
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Great! Your business has no active red flags.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedRedFlags.map((flag) => (
              <div
                key={flag.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="text-xl sm:text-2xl flex-shrink-0">
                      {redFlagIcons[flag.type] || '⚠️'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 mb-2">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                          {flag.title}
                        </h3>
                        <div className="flex flex-wrap gap-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${severityColors[flag.severity]}`}>
                            {flag.severity}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[flag.status]}`}>
                            {flag.status}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {flag.description}
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-gray-500 dark:text-gray-400 space-y-1 sm:space-y-0">
                        <span>Detected {formatDate(flag.detectedAt)}</span>
                        <span className={`font-medium ${
                          flag.impact < 0 ? 'text-red-600 dark:text-red-400' : 
                          flag.impact > 0 ? 'text-green-600 dark:text-green-400' : 
                          'text-gray-500 dark:text-gray-400'
                        }`}>
                          Impact: {getImpactText(flag.impact)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 mt-3 sm:mt-0 sm:ml-4">
                    {flag.status === 'active' && (
                      <button className="text-xs bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">
                        Investigate
                      </button>
                    )}
                    {flag.status === 'investigating' && (
                      <button className="text-xs bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-800 dark:text-green-300 px-2 py-1 rounded">
                        Mark Resolved
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Show More Link */}
        {maxItems && redFlags.length > maxItems && (
          <div className="mt-4 text-center">
            <a
              href="/alerts"
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
            >
              View all {redFlags.length} red flags →
            </a>
          </div>
        )}
      </div>
    </div>
  )
}